import { createHash, randomUUID } from 'node:crypto';
import { Router } from 'express';
import { z } from 'zod';
import { requireAuth, type AuthenticatedRequest } from './auth';
import { pool } from './db';
import { createLiveKitAdmission, liveKitReady } from './liveKitMedia';
import { createRateLimit } from './rateLimit';
import { authorizedContext, publicCallState, readSlots } from './familyCallState';
import { journeyNotificationRecipients, notifyUsers } from './notifications';
import { recordJourneyEvent } from './relationshipEvents';

export const familyCallRouter = Router();
const joinRateLimit = createRateLimit({
  windowMs: 60_000,
  maxRequests: 12,
  message: 'Too many call admission attempts. Try again shortly.',
});

familyCallRouter.get('/journeys/:id/call', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    if (!context) return;
    response.json({ call: await publicCallState(context, request.authUser!.id) });
  });

familyCallRouter.post('/journeys/:id/call/schedule-confirm', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    if (!context || !context.meetingId) {
      if (context) response.status(409).json({ error: 'No meeting is available' });
      return;
    }
    if (context.meetingStatus !== 'confirmed') {
      response.status(409).json({ error: 'Both primary participants must confirm the schedule first' });
      return;
    }
    const slots = await readSlots(context);
    const actorSlot = slots.find((slot) => slot.userId === request.authUser!.id);
    if (!actorSlot?.role.endsWith('Guardian')) {
      response.status(403).json({ error: 'A selected guardian seat is required' });
      return;
    }
    await pool.query(
      `INSERT INTO meeting_schedule_confirmations (meeting_id,user_id)
       VALUES ($1,$2) ON CONFLICT DO NOTHING`,
      [context.meetingId, request.authUser!.id],
    );
    await pool.query(
      "INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'meeting_schedule_confirmed',$2::jsonb)",
      [request.authUser!.id, JSON.stringify({ journeyId: context.journeyId, meetingId: context.meetingId })],
    );
    await recordJourneyEvent(context.journeyId, request.authUser!.id, 'meeting_confirmed', {
      meetingId: context.meetingId, guardianSchedule: true,
    });
    const call = await publicCallState(context, request.authUser!.id);
    const recipients = await journeyNotificationRecipients(context.journeyId, request.authUser!.id);
    await notifyUsers(recipients.userIds, {
      type: 'schedule_confirmed', title: 'تأكيد جديد للموعد',
      body: `${request.authUser!.displayName} أكد حضوره لمكالمة الفيديو.`,
      journeyId: context.journeyId, requestId: recipients.requestId,
      dedupeKey: `schedule-confirmed:${context.meetingId}:${request.authUser!.id}`,
    });
    if (call.allPartiesConfirmed) {
      const allRecipients = await journeyNotificationRecipients(context.journeyId);
      await notifyUsers(allRecipients.userIds, {
        type: 'schedule_confirmed', title: 'اكتملت موافقات الموعد',
        body: 'وافق الطرفان ووليّا الأمر على الموعد. يمكن الدخول إلى الجلسة بعد اكتمال بقية المتطلبات.',
        journeyId: context.journeyId, requestId: allRecipients.requestId,
        dedupeKey: `schedule-confirmed-all:${context.meetingId}`,
      });
    }
    response.json({ call });
  });

familyCallRouter.get('/journeys/:id/guardian-view', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    if (!context) return;
    const slots = await readSlots(context);
    const actorSlot = slots.find((slot) => slot.userId === request.authUser!.id);
    if (!actorSlot?.role.endsWith('Guardian')) {
      response.status(403).json({ error: 'Selected family representative required' }); return;
    }
    const profiles = await pool.query<{
      userId: string; displayName: string; gender: string | null;
      birthYear: number | null; birthPlace: string; country: string; city: string;
      maritalStatus: string | null; childrenCount: number | null;
      education: string | null; occupation: string; bio: string; valuesText: string;
    }>(
      `SELECT user_id AS "userId",display_name AS "displayName",gender,
              birth_year AS "birthYear",birth_place AS "birthPlace",country,city,
              marital_status AS "maritalStatus",children_count AS "childrenCount",
              education,occupation,bio,values_text AS "valuesText"
       FROM profiles WHERE user_id=ANY($1::uuid[])`,
      [[context.firstId, context.secondId]],
    );
    response.json({
      people: slots.map((slot) => ({
        role: slot.role, displayName: slot.displayName,
        isRepresentative: slot.isRepresentative,
        profile: profiles.rows.find((profile) => profile.userId === slot.userId) ?? null,
      })),
    });
  });

familyCallRouter.put('/journeys/:id/call-guardian', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    const body = z.discriminatedUnion('mode', [
      z.object({ mode: z.literal('guardian'), guardianUserId: z.uuid() }),
      z.object({ mode: z.literal('exception') }),
    ]).safeParse(request.body);
    if (!context || !body.success) { if (context) response.status(400).json({ error: 'Invalid guardian selection' }); return; }
    const actorId = request.authUser!.id;
    if (context.journeyStatus !== 'active' || ![context.firstId, context.secondId].includes(actorId)) {
      response.status(403).json({ error: 'Only active journey participants select guardians' }); return;
    }
    if (context.meetingId) {
      const started = await pool.query(
        'SELECT 1 FROM meeting_call_sessions WHERE meeting_id=$1 LIMIT 1', [context.meetingId],
      );
      if (started.rowCount) { response.status(409).json({ error: 'Guardian cannot change after the call begins' }); return; }
    }
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      let guardianUserId: string | null = null;
      let verificationId: string | null = null;
      let exceptionId: string | null = null;
      if (body.data.mode === 'guardian') {
        const eligible = await client.query<{ id: string }>(
          `SELECT i.id FROM guardian_invitations i JOIN users u ON u.id=i.accepted_by
           WHERE i.candidate_user_id=$1 AND i.accepted_by=$2 AND i.status='verified'
             AND u.is_active AND u.account_type IN ('candidate','guardian')`, [actorId, body.data.guardianUserId],
        );
        if (!eligible.rows[0]) {
          await client.query('ROLLBACK'); response.status(404).json({ error: 'A verified guardian relationship is required' }); return;
        }
        guardianUserId = body.data.guardianUserId;
        verificationId = eligible.rows[0].id;
      } else {
        const eligible = await client.query<{ id: string; representative_user_id: string }>(
          `SELECT e.id,e.representative_user_id FROM guardian_exceptions e
             JOIN users u ON u.id=e.representative_user_id
             JOIN guardian_representative_invitations ri ON ri.exception_id=e.id
           WHERE e.candidate_user_id=$1 AND e.status='approved'
             AND e.contact_confirmed_at IS NOT NULL
             AND ri.status='accepted' AND ri.representative_user_id=u.id
             AND u.is_active AND u.account_type='representative'
           ORDER BY reviewed_at DESC LIMIT 1`, [actorId],
        );
        if (!eligible.rows[0]) {
          await client.query('ROLLBACK'); response.status(404).json({ error: 'An approved no-guardian exception is required' }); return;
        }
        exceptionId = eligible.rows[0].id;
        guardianUserId = eligible.rows[0].representative_user_id;
      }
      if ([context.firstId, context.secondId].includes(guardianUserId!)) {
        await client.query('ROLLBACK');
        response.status(409).json({ error: 'A journey participant cannot occupy a representative seat' }); return;
      }
      const previousGuardian = await client.query<{ guardian_user_id: string }>(
        `SELECT guardian_user_id FROM journey_call_guardians
         WHERE journey_id=$1 AND participant_user_id=$2`,
        [context.journeyId, actorId],
      );
      const alreadySelected = await client.query(
        `SELECT 1 FROM journey_call_guardians WHERE journey_id=$1
         AND participant_user_id<>$2 AND guardian_user_id=$3`,
        [context.journeyId, actorId, guardianUserId],
      );
      if (alreadySelected.rowCount) {
        await client.query('ROLLBACK');
        response.status(409).json({ error: 'Each family seat requires a distinct account' }); return;
      }
      await client.query(
        `UPDATE journey_memberships SET is_active=FALSE
         WHERE journey_id=$1 AND user_id IN (
           SELECT guardian_user_id FROM journey_call_guardians
           WHERE journey_id=$1 AND participant_user_id=$2
         ) AND user_id<>$3`, [context.journeyId, actorId, guardianUserId],
      );
      const roleLabel = body.data.mode === 'guardian' ? 'ولي الأمر' : 'الممثل البديل';
      const membership = await client.query(
        'SELECT invitation_id FROM journey_memberships WHERE journey_id=$1 AND user_id=$2',
        [context.journeyId, guardianUserId],
      );
      if (membership.rowCount) {
        await client.query(
          `UPDATE journey_memberships SET is_active=TRUE,permission='viewer',role_label=$3
           WHERE journey_id=$1 AND user_id=$2`, [context.journeyId, guardianUserId, roleLabel],
        );
      } else {
        const familyInvitationId = randomUUID();
        await client.query(
          `INSERT INTO family_invitations
             (id,journey_id,invited_by,code_hash,role_label,permission,status,expires_at,accepted_by,accepted_at)
           VALUES ($1,$2,$3,$4,$5,'viewer','accepted',NOW()+INTERVAL '100 years',$6,NOW())`,
          [familyInvitationId, context.journeyId, actorId,
            createHash('sha256').update(randomUUID()).digest('hex'), roleLabel, guardianUserId],
        );
        await client.query(
          `INSERT INTO journey_memberships
             (journey_id,user_id,invitation_id,role_label,permission)
           VALUES ($1,$2,$3,$4,'viewer')`,
          [context.journeyId, guardianUserId, familyInvitationId, roleLabel],
        );
      }
      await client.query(
        `INSERT INTO journey_call_guardians
           (journey_id,participant_user_id,guardian_user_id,selection_mode,verification_id,exception_id)
         VALUES ($1,$2,$3,$4,$5,$6) ON CONFLICT (journey_id,participant_user_id)
         DO UPDATE SET guardian_user_id=EXCLUDED.guardian_user_id,
           selection_mode=EXCLUDED.selection_mode,verification_id=EXCLUDED.verification_id,
           exception_id=EXCLUDED.exception_id,updated_at=NOW()`,
        [context.journeyId, actorId, guardianUserId, body.data.mode, verificationId, exceptionId],
      );
      if (context.meetingId && previousGuardian.rows[0]?.guardian_user_id !== guardianUserId) {
        await client.query(
          `DELETE FROM meeting_schedule_confirmations
           WHERE meeting_id=$1 AND user_id=ANY($2::uuid[])`,
          [context.meetingId, [previousGuardian.rows[0]?.guardian_user_id, guardianUserId].filter(Boolean)],
        );
      }
      await client.query('COMMIT');
    } catch (error) {
      await client.query('ROLLBACK');
      if (typeof error === 'object' && error !== null && 'code' in error && error.code === '23505') {
        response.status(409).json({ error: 'The same guardian cannot represent both participants' }); return;
      }
      throw error;
    } finally { client.release(); }
    await pool.query(
      "INSERT INTO audit_events (user_id, event_type, details) VALUES ($1, 'call_guardian_selected', $2::jsonb)",
      [actorId, JSON.stringify({ journeyId: context.journeyId, mode: body.data.mode,
        guardianUserId: body.data.mode === 'guardian' ? body.data.guardianUserId : null,
        representativeSelected: body.data.mode === 'exception' })],
    );
    response.json({ call: await publicCallState(context, actorId) });
  });

const sessionSchema = z.object({ sessionId: z.uuid() });
const readOwnedSession = async (meetingId: string, actorId: string, sessionId: string) => {
  const result = await pool.query<{
    session_id: string; user_id: string;
  }>(
    `SELECT session_id, user_id FROM meeting_call_sessions
     WHERE meeting_id=$1 AND user_id=$2 AND session_id=$3 AND left_at IS NULL
       AND last_seen_at >= NOW()-INTERVAL '20 seconds'`,
    [meetingId, actorId, sessionId],
  );
  return result.rows[0] ?? null;
};

familyCallRouter.post('/journeys/:id/call/join', requireAuth, joinRateLimit,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    if (!context || !context.meetingId) { if (context) response.status(409).json({ error: 'No meeting is available' }); return; }
    const state = await publicCallState(context, request.authUser!.id);
    if (!state.canJoin) { response.status(409).json({ error: 'Call is not ready for this account or time' }); return; }
    if (!liveKitReady()) {
      response.status(503).json({ error: 'LiveKit Cloud is not configured' }); return;
    }
    const id = randomUUID();
    const actorSlot = state.slots.find((slot) => slot.userId === request.authUser!.id);
    if (!actorSlot) { response.status(403).json({ error: 'A reserved call seat is required' }); return; }
    const admission = await createLiveKitAdmission({
      meetingId: context.meetingId,
      sessionId: id,
      userId: request.authUser!.id,
      displayName: request.authUser!.displayName,
      role: actorSlot.role,
    });
    try {
      await pool.query(
        `INSERT INTO meeting_call_sessions (session_id, meeting_id, user_id) VALUES ($1,$2,$3)`,
        [id, context.meetingId, request.authUser!.id],
      );
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'code' in error && error.code === '23505') {
        response.status(409).json({ error: 'This account is already in the call on another device' }); return;
      }
      throw error;
    }
    await pool.query(
      "INSERT INTO audit_events (user_id, event_type, details) VALUES ($1, 'family_call_joined', $2::jsonb)",
      [request.authUser!.id, JSON.stringify({ journeyId: context.journeyId, meetingId: context.meetingId })],
    );
    const roleLabels = {
      groom: 'العريس', groomGuardian: 'ولي أمر العريس',
      bride: 'العروس', brideGuardian: 'ولي أمر العروس',
    } as const;
    await recordJourneyEvent(context.journeyId, request.authUser!.id, 'call_joined', {
      meetingId: context.meetingId, role: actorSlot.role, roleLabel: roleLabels[actorSlot.role],
    });
    const currentCall = await publicCallState(context, request.authUser!.id);
    if (currentCall.guardiansPresent) {
      await notifyUsers([context.firstId, context.secondId], {
        type: 'call_ready', title: 'الجلسة متاحة للدخول',
        body: 'دخل وليّا الأمر إلى الجلسة، ويمكنك الانضمام الآن.',
        journeyId: context.journeyId, dedupeKey: `call-ready:${context.meetingId}`,
      });
    }
    response.status(201).json({
      sessionId: id,
      call: currentCall,
      media: admission,
    });
  });

familyCallRouter.get('/journeys/:id/call/state', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    const sessionId = z.uuid().safeParse(request.query.sessionId);
    if (!context || !sessionId.success || !context.meetingId) {
      if (context) response.status(400).json({ error: 'Invalid call state request' }); return;
    }
    const state = await publicCallState(context, request.authUser!.id);
    const ownedSession = await readOwnedSession(
      context.meetingId,
      request.authUser!.id,
      sessionId.data,
    );
    const canRemain = context.journeyStatus === 'active' && !context.callEndedAt
      && state.pairReady && state.guardiansReady && state.allPartiesConfirmed
      && state.paymentsReady && state.callWindowOpen
      && state.slots.some((slot) => slot.userId === request.authUser!.id);
    if (!canRemain || !ownedSession) {
      response.status(403).json({ error: 'Call session is no longer authorized' }); return;
    }
    await pool.query('UPDATE meeting_call_sessions SET last_seen_at=NOW() WHERE session_id=$1', [sessionId.data]);
    const refreshed = await publicCallState(context, request.authUser!.id);
    response.json({ call: { ...refreshed, canJoin: true } });
  });

familyCallRouter.post('/journeys/:id/call/connected', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    const parsed = z.object({ sessionId: z.uuid(), peerSessionId: z.uuid() }).safeParse(request.body);
    if (!context || !parsed.success || !context.meetingId) {
      if (context) response.status(400).json({ error: 'Invalid call connection report' }); return;
    }
    const { sessionId, peerSessionId } = parsed.data;
    const state = await publicCallState(context, request.authUser!.id);
    if (sessionId === peerSessionId
      || !await readOwnedSession(context.meetingId, request.authUser!.id, sessionId)
      || !state.slots.some((slot) => slot.sessionId === peerSessionId)) {
      response.status(403).json({ error: 'Peer connection is not authorized' }); return;
    }
    const lower = sessionId < peerSessionId ? sessionId : peerSessionId;
    const upper = sessionId < peerSessionId ? peerSessionId : sessionId;
    const confirmed = await pool.query<{ confirmed: boolean }>(
      `INSERT INTO meeting_call_links
         (meeting_id, lower_session_id, upper_session_id, lower_confirmed, upper_confirmed)
       VALUES ($1,$2,$3,$4,$5)
       ON CONFLICT (lower_session_id, upper_session_id) DO UPDATE
         SET lower_confirmed=meeting_call_links.lower_confirmed OR EXCLUDED.lower_confirmed,
             upper_confirmed=meeting_call_links.upper_confirmed OR EXCLUDED.upper_confirmed,
             updated_at=NOW()
       RETURNING lower_confirmed AND upper_confirmed AS confirmed`,
      [context.meetingId, lower, upper, sessionId === lower, sessionId === upper],
    );
    response.json({ confirmed: confirmed.rows[0].confirmed });
  });

familyCallRouter.post('/journeys/:id/call/leave', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const context = await authorizedContext(request, response);
    const parsed = sessionSchema.safeParse(request.body);
    if (!context || !parsed.success || !context.meetingId) {
      if (context) response.status(400).json({ error: 'Invalid call session' }); return;
    }
    const left = await pool.query(
      `UPDATE meeting_call_sessions SET left_at=NOW()
       WHERE session_id=$1 AND meeting_id=$2 AND user_id=$3 AND left_at IS NULL
       RETURNING session_id`,
      [parsed.data.sessionId, context.meetingId, request.authUser!.id],
    );
    if (left.rowCount) {
      const slot = (await readSlots(context)).find((item) => item.userId === request.authUser!.id);
      const roleLabels = {
        groom: 'العريس', groomGuardian: 'ولي أمر العريس',
        bride: 'العروس', brideGuardian: 'ولي أمر العروس',
      } as const;
      await recordJourneyEvent(context.journeyId, request.authUser!.id, 'call_left', {
        meetingId: context.meetingId, role: slot?.role,
        roleLabel: slot ? roleLabels[slot.role] : undefined,
      });
    }
    response.json({ left: true });
  });
